import React, { useMemo } from 'react';
import { QueryFactory } from 'services/api';
import { SigningRoleIcon } from './SigningRoleIcon.tsx';
import styles from './SignatureBlock.module.scss';
import { AppMenuItem } from 'components/uikit/menu/AppMenu.tsx';
import { AddMenu } from 'components/uikit/menu/AddMenu.tsx';
import { MultipleSigningRoleIcon } from './MultipleSigningRoleIcon.tsx';
import { createId } from 'components/uikit/type-utils.ts';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { useSignatureBlockQueriesInvalidation } from './useSignatureBlockQueriesInvalidation.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import {
  OperationType,
  ProjectResourceType,
} from 'services/api/api-client.types.ts';

export type SignatureBlockProps = {
  projectId: number;
  revisionId: number;
  documentId: number;
};

export const SignatureBlock: React.FC<SignatureBlockProps> = (props) => {
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    documentId: props.documentId,
    revisionId: props.revisionId,
  });
  const modal = useModal();
  const i18n = useScopedTranslation(
    'Component.DocumentRoleDeleteConfirmationModal',
  );
  const i18n_button = useScopedTranslation('Button.Title');

  const projectRoles = QueryFactory.ProjectRolesQuery.useSearchQuery({
    projectId: props.projectId,
    limit: 1,
    hideSpecialRoles: true,
  });

  const availableRoles =
    QueryFactory.DocumentSigningQuery.useGetDocumentSignersAvailableRolesQuery(
      props.revisionId,
      {
        enabled: !!props.revisionId,
      },
    );

  const signingStatuses =
    QueryFactory.DocumentSigningQuery.useGetDocumentsSigningStatusQuery(
      props.revisionId,
      {
        enabled: !!props.revisionId,
        staleTime: 5 * 60 * 1000,
      },
    );
  const signingStatus = signingStatuses.data?.find(
    (dto) => dto.documentId === props.documentId,
  );

  const canAddRole =
    (availableRoles.data?.find((dto) => dto.documentId === props.documentId)
      ?.roles.length ?? 0) > 0;

  const addRole = async (documentId: number, roleId: number) => {
    try {
      await QueryFactory.DocumentSigningClient.addDocumentSignersRole(
        documentId,
        roleId,
      );
      await invalidateSignatureBlockQueries();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const removeRole = async (documentId: number, roleId: number) => {
    try {
      const result = await modal.showConfirm({
        title: i18n.t('title'),
        text: i18n.t('text'),
        okButtonText: i18n_button.t('delete'),
        okButtonColor: ButtonColor.DestroyPrimary,
        cancelButtonColor: ButtonColor.Secondary,
      });

      if (!result) return;

      await QueryFactory.DocumentSigningClient.removeDocumentSignersRole(
        documentId,
        roleId,
      );
      await invalidateSignatureBlockQueries();
    } catch (e) {
      showErrorToast(e);
    }
  };

  const menuItems = useMemo(() => {
    const items: AppMenuItem[] = [];

    for (const item of availableRoles.data?.find(
      (dto) => dto.documentId === props.documentId,
    )?.roles ?? []) {
      items.push({
        key: item.id,
        text: item.name,
        secondaryTexts: item.signers?.map(
          (signer) => `${signer.signer.firstName} ${signer.signer.lastName}`,
        ),
        className: styles.menuItem,
        onClick: async () => {
          await addRole(props.documentId, item.id);
        },
      });
    }
    return items;
  }, [availableRoles.data]);

  const icons: React.JSX.Element[] = useMemo(() => {
    const rolesAmount: number = 5;
    if (!signingStatus?.roles) return [];
    const roles = signingStatus?.roles;

    if (roles.length <= rolesAmount) {
      return roles.map((dto, index) => (
        <SigningRoleIcon
          dto={dto}
          onRemove={(roleId) => removeRole(props.documentId, roleId)}
          key={`${dto.role.id}-${index}`}
        />
      ));
    } else {
      const result: React.JSX.Element[] = [];
      const rolesToDisplay = roles.slice(0, rolesAmount - 1);
      const rolesToHide = roles.slice(rolesAmount - 1);
      rolesToDisplay.forEach((dto, index) =>
        result.push(
          <SigningRoleIcon
            dto={dto}
            onRemove={(roleId) => removeRole(props.documentId, roleId)}
            key={`${dto.role.id}-${index}`}
          />,
        ),
      );
      if (rolesToHide.length > 0) {
        result.push(
          <MultipleSigningRoleIcon
            dtos={rolesToHide}
            key={createId()}
            onRemove={(roleId) => removeRole(props.documentId, roleId)}
          />,
        );
      }
      return result;
    }
  }, [signingStatus?.roles, props.documentId, props.revisionId]);

  return (
    <div className={styles.signatureBlock}>
      {(availableRoles.isLoading || signingStatuses.isLoading) && (
        <Loading loading className={styles.placeholder} />
      )}
      {!availableRoles.isLoading && !signingStatuses.isLoading && (
        <div className={styles.wrapper} data-test-id={'signatureBlock'}>
          {icons}
          {projectRoles.data?.totalCount != 0 && (
            <PermissionGate
              permissions={[
                permission(
                  ProjectResourceType.ProjectRole,
                  OperationType.Update,
                ),
                permission(ProjectResourceType.Document, OperationType.Update),
              ]}
            >
              <div className={styles.addMenu}>
                <AddMenu enabled={canAddRole} menuItems={menuItems} />
              </div>
            </PermissionGate>
          )}
        </div>
      )}
    </div>
  );
};
